import axios, {
  AxiosError,
  AxiosHeaders,
  AxiosInstance,
  AxiosProgressEvent,
  AxiosRequestConfig,
  AxiosRequestTransformer,
  AxiosResponse,
} from 'axios';
import MockAdapter from 'axios-mock-adapter';

import { ArgumentNullError, isDefined, isEmpty, isNotDefined } from '@hsp/utilities';

import { strings } from 'constants/index';
import { buildCancellationHandlers, dateTransformer } from 'services/http/utilities';
import { StatusCode } from 'types/enums/status-code';
import { ICancellationRequestHandler, IFileUploadOptions, IHttpServiceOptions, IMocked } from 'types/services/http';
import { isDev } from 'utilities/is-dev';

import { addJwtTokenInterceptor, addLoggingInterceptor, addResponseInterceptor } from './interceptors';

const headers = new AxiosHeaders()
  .set('accept', 'application/json')
  .set('content-type', 'application/json; charset=utf-8;');

/**
 * Base class for all API end point service classes.
 */
export abstract class BaseHttpService {
  protected cancellations: Record<string, ICancellationRequestHandler> = {};

  private readonly baseApiUri: string;

  private instance: AxiosInstance | null = null;

  private mocked: IMocked | null = null;

  protected constructor(options?: IHttpServiceOptions) {
    const { endpoints = [], uri = process.env.REACT_APP_API_BASE_URL ?? '' } = options ?? {};

    if (isNotDefined(uri) || isEmpty(uri)) {
      throw new ArgumentNullError('Uri');
    }

    this.baseApiUri = uri;
    this.baseApiUri += this.baseApiUri.endsWith('/') ? '' : '/';
    this.cancellations = buildCancellationHandlers(endpoints, this.cancellations);
  }

  protected get axios(): AxiosInstance {
    return isNotDefined(this.instance) ? this.initialize() : this.instance;
  }

  cancel(uri: string): void {
    const { cancellations } = this;

    if (isNotDefined(cancellations)) {
      return;
    }

    const handler = cancellations[uri];

    if (isNotDefined(handler)) {
      return;
    }

    const controller = handler.onCancellation();

    controller.abort();
  }

  /**
   * Call to begin setting up a mock of this instance.
   * @param {number} delay The amount time to delay a response
   * @returns {IMocked | null}
   */
  mock(delay?: number): IMocked | null {
    if (isNotDefined(this.mocked)) {
      const adapter = new MockAdapter(this.axios, {
        delayResponse: delay,
        onNoMatch: 'throwException',
      });

      this.mocked = {
        adapter,
        reset: () => adapter.reset(),
        restore: () => {
          adapter.restore();
          this.mocked = null;
        },
      };
    }

    return this.mocked;
  }

  /**
   * Creates an instance of the axios client.
   * @returns
   */
  protected createInstance(isSecure = true): AxiosInstance {
    let requestConfig = {
      baseURL: this.baseApiUri,
      headers,
      responseType: 'json',
      transformRequest: [dateTransformer, ...(axios.defaults.transformRequest as AxiosRequestTransformer[])],
    } as AxiosRequestConfig;

    if (isSecure) {
      requestConfig = {
        ...requestConfig,
        withCredentials: true,
      };
    }

    const http = axios.create(requestConfig);

    this.addInterceptors(http, isSecure);

    return http;
  }

  protected defaultRequestConfig<TRequest>(url: string, cancel = false): AxiosRequestConfig<TRequest> {
    return {
      signal: this.createSignal(url, cancel),
      url,
    };
  }

  /**
   * Performs a DELETE method request to an API endpoint.
   * @param config
   * @returns
   */
  protected delete<T = unknown, R = AxiosResponse<T>>(
    config: AxiosRequestConfig,
    instance?: AxiosInstance
  ): Promise<R> {
    config = {
      ...config,
      baseURL: this.baseApiUri,
      method: 'DELETE',
    };

    return this.request(config, instance);
  }

  /**
   * Performs a GET method request to an API endpoint.
   * @param config
   * @returns
   */
  protected get<T = unknown, R = T>(config: AxiosRequestConfig, instance?: AxiosInstance): Promise<R> {
    config = {
      ...config,
      baseURL: this.baseApiUri,
      method: 'GET',
    };

    return this.request(config, instance);
  }

  protected handleError(error: AxiosError): Promise<AxiosError> {
    if (typeof error === 'string') {
      return Promise.reject(error);
    }

    const { request, response } = error;

    if (isNotDefined(request) && isNotDefined(response)) {
      let { message } = error;

      message = message ?? strings.Api.UnknownError;

      return Promise.reject(message);
    }

    const { data, status } = response ?? request;

    switch (status) {
      case StatusCode.Forbidden:
        break;

      case StatusCode.InternalServerError:
        break;

      case StatusCode.TooManyRequests:
        break;

      case StatusCode.Unauthorized:
        break;

      case StatusCode.UnprocessableEntity:
        return Promise.resolve(data);
    }

    return Promise.reject(isDefined(data) && !isEmpty(data) ? data : error.message);
  }

  /**
   * Performs a POST method request to an API endpoint.
   * @param data
   * @param config
   * @returns
   */
  protected post<T = unknown, TData = T, R = T>(
    data: TData,
    config?: AxiosRequestConfig<TData>,
    instance?: AxiosInstance
  ): Promise<R> {
    config = {
      ...config,
      baseURL: this.baseApiUri,
      data,
      method: 'POST',
    };

    return this.request(config, instance);
  }

  /**
   * Performs a PUT method request to an API endpoint.
   * @param data
   * @param config
   * @returns
   */
  protected put<T = unknown, TData = T, R = T>(
    data: TData,
    config?: AxiosRequestConfig<TData>,
    instance?: AxiosInstance
  ): Promise<R> {
    config = {
      ...config,
      baseURL: this.baseApiUri,
      data,
      method: 'PUT',
    };

    return this.request(config, instance);
  }

  /**
   * Upload file(s) to an API endpoint using the POST method.
   * @param {File | File[] | FileList } file The file(s) that will be uploaded.
   * @param {IFileUploadOptions} options The options used to configure this upload request.
   * @param {AxiosRequestConfig<FormData>} config The request configuration
   * @returns {Promise<R>}
   */
  protected upload<T = unknown, R = T>(
    file?: File | File[] | FileList,
    options?: IFileUploadOptions,
    config?: AxiosRequestConfig<FormData>,
    instance?: AxiosInstance
  ): Promise<R> {
    const { formData, method = 'POST', name = 'file', notify } = options ?? {};
    const data: FormData = formData ?? new FormData();

    if (isDefined(file)) {
      if (file instanceof FileList) {
        file = Array.from(file);
      } else if (!Array.isArray(file)) {
        file = [file];
      }

      file.forEach((item) => data.append(name, item));
    }

    config = {
      ...config,
      baseURL: this.baseApiUri,
      data,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      method,
      onUploadProgress: ({ loaded, total }: AxiosProgressEvent): void => {
        if (isNotDefined(total)) {
          return;
        }

        const progress = (loaded / total) * 100;

        notify?.(progress);
      },
    };

    return this.request(config, instance);
  }

  /**
   * Adds an interceptor into the stack.
   */
  private addInterceptors(http: AxiosInstance, isSecure: boolean): void {
    if (isDev) {
      addLoggingInterceptor(http);
    }
    if (isSecure) {
      addJwtTokenInterceptor(http);
    }
    addResponseInterceptor(http, this.handleError.bind(this));
  }

  private createSignal(url: string, cancel = false) {
    let handler = this.cancellations[url];

    if (isNotDefined(handler)) {
      this.cancellations = buildCancellationHandlers([url], this.cancellations);

      handler = this.cancellations[url];
    }

    return cancel ? handler.onCancellation().signal : undefined;
  }

  private initialize(): AxiosInstance {
    const http = this.createInstance();

    this.instance = http;

    return http;
  }

  private request<T = unknown, R = T>(config: AxiosRequestConfig, instance?: AxiosInstance): Promise<R> {
    if (isDefined(instance)) {
      return instance.request(config);
    }

    return this.axios.request(config);
  }
}
